import { UserDTO } from "@dto/usersResponse.dto";
import { ValidatorRule } from '@rc-component/form/es/interface';
import { App } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { Location } from "react-router-dom";
import { AppState } from "src/App";
import { useAppGlobStore } from "src/appGlobalStore";
import { Label } from "src/data/Label";
import { DateUtil } from "../common/util/DateUtil";
import { AllType, RoutePath } from '../data/Routes';
import { MenuNode } from '../model/MenuNode';
import { Auth } from "../service/auth.service";
import { MenuService } from "../service/menu.service";
import { Deferred } from "./Deferred";

type ReturnTypePromise<T> = T extends (...args: unknown[]) => Promise<infer R> ? R : unknown;

const PATH_NO_DEVICE = RoutePath.SETTINGS + RoutePath.CONFIGURATION + RoutePath.STORAGE;
const PATH_DEFAULT_DASHBOARD = RoutePath.DASHBOARD + RoutePath.DEFAULT;

declare const publicUrl: string;

/**
 * Static utility class
 */
export class GLOB {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() { }

  /**
   * Checks PROD / DEV UI build
   * @returns true if running production build
   */
  static isProdBuild() {
    return import.meta.env.PROD;
  }

  /**
   * Checks PROD / DEV environment set on BE
   * @returns true if NODE_ENV=production set on BE
   */
  static isProdEnv() {
    return useAppGlobStore.getState().info.backend.production || process.env.DUMMY_BE_PROD;
  }

  /**
   * Get context URL path
   */
  static getPublicUrl(): string {
    return GLOB.isProdBuild() && publicUrl || import.meta.env.VITE_PUBLIC_URL;
  }

  /**
   *  URL part preceding app PATH
   */
  static readonly SERVER_URL = import.meta.env.VITE_SERVER + (import.meta.env.PROD && GLOB.getPublicUrl() || '');

  /**
   * URL for WebSocket
   */
  static readonly SERVER_WS_URL = (import.meta.env.VITE_SERVER_WS ||
    ((window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host)) +
    (import.meta.env.PROD && GLOB.getPublicUrl() || '');

  /**
   * Reference to Auth Service
   */
  static readonly authService = new Auth();

  /**
   * Reference for Menu Service
   */
  static readonly menuService = new MenuService();

  /**
   * Resolved when left tree menu is loaded
   */
  static readonly treeLoaded = new Deferred();

  /**
   * Resolved when backend info is obtained
   */
  static readonly infoLoaded = new Deferred();

  /**
   * User is logged in and required requests are resolved
   */
  static readonly xormonReady = new Deferred();

  /**
   * Load only graph items upto this limit
   */
  static graphTopItems = 20;

  /**
   * Hide or compact deleted items in graph
   */
  static showDeletedItems = false;

  /**
   * Optional label prepended to the browser tab title to distinguish multiple instances
   */
  static instanceLabel = '';

  static readonly SHOW_RELEASE_INFO_KEY = 'showReleaseInfo';

  /**
   * Decimal separator
   */

  static decimalSeparator = '.';

  /**
   * CSV separator
   */
  static csvSeparator = ';';

  /**
   * Show what's new dialog after login
   */
  static showReleaseInfo = !!sessionStorage.getItem(GLOB.SHOW_RELEASE_INFO_KEY);

  /**
   * Application upgrade indicator
   */
  static isUpgrading = false;

  /**
   * True if Grafana configured
   */
  static grafanaActive: boolean;

  private static _userInfo: UserDTO;
  /**
   * Get currently logged-in user information
   */
  static get userInfo(): Readonly<UserDTO> { return GLOB._userInfo; }
  static set userInfo(value: UserDTO) {
    GLOB._userInfo = value;
    DateUtil.setOffset(value.timezone);
  }

  //#region routing

  /**
   * Router location
   */
  static location: Location;

  /**
   * AbortController for navigation
   */
  static navAborter: AbortController;

  /**
   * Class path e.g.: /storage
   */
  static selectedClass: RoutePath;// string;
  /**
   * All type path </type, /group, /device>
   */
  static selectedAllType: AllType;
  /**
   * Storage group name e.g.: DC1
   */
  static selectedGroup: string;
  /**
   * HW type path when allType=<null|type> e.g. /ISILON
   * or device path when allType=<group|device> e.g. /Isilon200
   */
  static selectedType: string;

  private static _selectedItem: MenuNode;
  /**
   * Left menu tree node according to last part of current route
   */
  static get selectedItem(): MenuNode {
    return this._selectedItem;
  }
  static set selectedItem(item: MenuNode) {
    this._selectedItem = item;
    setTimeout(() => {
      if (!useAppGlobStore.getState().leftMenuSelectedKeys || !useAppGlobStore.getState().leftMenuExpandedKeys) return;
      if (item) {
        useAppGlobStore.setState(prev => ({
          leftMenuSelectedKeys: [item.key],
          leftMenuExpandedKeys: [...prev.leftMenuExpandedKeys,
          ...MenuNode.getParentKeys(item.isLeaf ? item.parent : item)].filter(GLOB.uniqueArrayFilter)
        }));
      }
      else {
        useAppGlobStore.setState({ leftMenuSelectedKeys: [], leftMenuExpandedKeys: [] });
      }
      GLOB.updateTitle();
    });
  }

  static updateTitle() {
    const appName = GLOB.instanceLabel || Label.XORMON_NG;
    const item = GLOB._selectedItem;
    if (item) {
      document.title = appName + ' - ' + MenuNode.getParentNodes(item).map(mn => mn.titleText || mn.title).join(' | ');
    } else {
      document.title = appName;
    }
  }

  static getGroup(node: MenuNode = GLOB.selectedItem): MenuNode {
    if (!node?.parent) return;
    if (node.parent?.path === RoutePath.GROUP) return node;
    return GLOB.getGroup(node.parent);
  }

  //#endregion

  //#region state

  /**
   * Scroll left menu tree to show selected item
   */
  static scrollSelectedItemIntoView: () => void;

  static getAppState: () => Readonly<AppState>;
  static setAppState: <K extends keyof AppState>(
    state:
      | ((prevState: Readonly<AppState>, props: Readonly<unknown>) => Pick<AppState, K> | AppState | null)
      | (Pick<AppState, K> | AppState | null),
    callback?: () => void
  ) => void;

  /**
   * Rerender app
   */
  static refresh() {
    if (GLOB.getAppState && GLOB.setAppState)
      GLOB.setAppState(prev => ({ refresh: !prev.refresh }));
  }

  /**
   * AntD app context hooks
   */
  static app: ReturnType<typeof App.useApp>;

  //#endregion

  //#region functions

  /**
   * Checks if certificate will expire within 30 days
   * @returns true if certificate expires soon
   */
  static isCertificateEnding() {
    const validTo = useAppGlobStore.getState().info?.backend?.cert?.validTo;
    return dayjs(validTo).diff(undefined, 'day') < 30;
  }

  /**
   * Checks if enterprise edition license is valid
   * @returns true if active enterprise license
   */
  static isValidEnterprise() {
    const backend = useAppGlobStore.getState().info.backend;
    return backend.premium.valid && !backend.free;
  }

  /**
   * Checks if license is free or expired trial
   * @returns true if free, false for valid or expired enterprise
   */
  static isFreeOrExpiredTrial() {
    const backend = useAppGlobStore.getState().info.backend;
    return backend.free && !backend.premium?.expirationDate;
  }

  private static getActiveMenuClasses() {
    return useAppGlobStore.getState().topMenu?.filter(mn => mn.notActive === false);
  }
  /**
   * Get default route path
   * @returns
   */
  static getDefaultPath() {
    return GLOB.getActiveMenuClasses().length || !GLOB.userInfo.isAdmin ? PATH_DEFAULT_DASHBOARD : PATH_NO_DEVICE;
  }

  /**
   * Get last path of URL
   * @param url
   * @returns
   */
  static getItemPath(item: { url?: string; group_id?: number; itemId?: string; }) {
    if (GLOB.selectedClass === RoutePath.DASHBOARD && item.itemId) {
      return '/' + item.itemId;
    }
    else if (item.url) {
      return item.url.substring(item.url.lastIndexOf('/'));
    } else if (item.group_id)
      return '/' + item.group_id;
  }

  /**
   * Returns promise, which is resolved after timeout with result of callback
   * @param callback
   * @param timeout
   * @returns
   */
  static timeoutPromise<T = void>(callback: () => T, timeout?: number) {
    return new Promise<T>(resolve => setTimeout(() => {
      resolve(callback ? callback() : null);
    }, timeout));
  }

  /**
   * Executes callback after specified number of extra timeout cycles
   * @param callback function to call
   * @param count number of extra timeout cycles, executes callback in first timeout when count <= 0 (same as setTimeout), default=1
   */
  static multiTimeout(callback: () => void, count = 1) {
    window.setTimeout(() => {
      if (count > 0)
        GLOB.multiTimeout(callback, count - 1);
      else
        callback();
    });
  }

  /**
   * Async debounce wrapper. Lodash debounce doesn't handle async functions.
   */
  static debounceAsync<T extends (...args: Parameters<T>) => Promise<ReturnTypePromise<T>>>(func: T, wait: number): (...args: Parameters<T>) => Promise<ReturnTypePromise<T>> {
    let timerID = -1;

    return (...args) => {
      window.clearTimeout(timerID);

      const promiseForFunc = new Promise((resolve) => {
        timerID = window.setTimeout(resolve, wait);
      });

      return promiseForFunc.then(() => func(...args));
    };
  }

  /**
   * Removes null values
   * @param value
   * @param index
   * @param array
   * @returns
   */
  static uniqueArrayFilter(value, index: number, array: unknown[]) {
    return value && array.indexOf(value) === index;
  }

  /**
   * Keeps null values
   * @param value
   * @param index
   * @param array
   * @returns
   */
  static uniqueArrayFilterNull(value, index: number, array: unknown[]) {
    return array.indexOf(value) === index;
  }

  /**
   * Sorter function, case insensitive, numeric collation '2<10'
   * @param a
   * @param b
   * @returns negative value if a before b, 0 if same, positive value if a after b
   */
  static naturalSort(a, b) {
    if (typeof a === 'number' && typeof b === 'number') {
      return a - b;
    }
    const as = typeof a === 'string' ? a : String(a);
    const bs = typeof b === 'string' ? b : String(b);
    return as?.localeCompare(bs, undefined, { sensitivity: 'base', numeric: true });
  }

  /**
   * Returns Boolean after check Regex pattern
   * @param value text to check
   * @returns true if input valid
   */
  private static isValidTitle(value: string) {
    const regex = /^[a-zA-Z0-9_&@,.À-ÖØ-öø-ÿěščřžýáíéúůňďťĚŠČŘŽÝÁÍÉÚŮŇĎŤа-яА-ЯІі\s:-]+$/;
    return regex.test(value);
  }
  static readonly TitleCharactersValidatorRule: ValidatorRule = {
    validator(rule, value: string, callback) {
      if (GLOB.isValidTitle(value)) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("Use only letters, numbers, and _ & @ , ."));
    }
  };

  static readonly URL_PATTERN = /^https?:\/\/.+/i;

  //#endregion
}

axios.defaults.baseURL = GLOB.SERVER_URL;
axios.defaults.withCredentials = true;
